import MarkerClusterer from '@google/markerclusterer';
import MarkerWithLabel from '@google/markerwithlabel';
import GoogleMapReact from 'google-map-react';
import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { africaIcon, ClusterMarker, MarkerIconGmap } from '../../components/svg/MarkerIcon';
import { StoreContext } from '../../context/StoreContext';
import { BIG_SCREEN } from '../../global';
import useCurrentRegion from '../../hooks/useCurrentRegion';
import useFetch from '../../hooks/useFetch';
import './Map.scss';

interface IZoomLevels {
  [key:string]: number;
}

// Zoom the map based on the size of the region.
const getZoomLevelByRegion = (regionId:string) => {
  const zoomLevels:IZoomLevels = {
    '1': BIG_SCREEN ? 14 : 13,
    '2': BIG_SCREEN ? 13 : 12,
    '3': BIG_SCREEN ? 11 : 10,
  };

  return zoomLevels[regionId] || 12;
};

// Draw the polygon on the map.
const drawRegion = (google:any, polygon:any, fill:boolean = true) => {
  const region = new google.maps.Polygon({
    fillColor: '#ffffff',
    fillOpacity: fill ? .5 : 0,
    geodesic: true,
    paths: polygon,
    strokeColor: '#419EDC',
    strokeOpacity: 1,
    strokeWeight: 2
  });

  region.setMap(google.map);
};

// Get the polygon(s) to draw on the map.
const setRegionsOnMap = (google:any, polygons:any, negativePolygons:any) => {
  let polygon:any = [];
  if (typeof polygons !== 'object') {
    polygon = JSON.parse(polygons);
  } else {
    polygons.map((coords:any) => {
      polygon.push(JSON.parse(coords));
    });
  }

  if (negativePolygons.length > 0) {
    polygon.push(negativePolygons);
  }

  drawRegion(google, polygon);
};

// Check the distances array for the length of the distance string.
// This will be used to determine the X position on the map for the distance block.
const getNumberLengthOfDistance = (distances:number[]) => {
  const checkTen = distances.map((distance) => distance.toString().replace('.00', '').length === 2);
  const checkDecimal = distances.map((distance) => distance.toString().replace('.00', '').length === 4);
  const checkDoubleDecimal = distances.map((distance) => distance.toString().replace('.00', '').length === 5);

  return {
    double: checkTen.includes(true),
    decimal: checkDecimal.includes(true),
    doubleDecimal: checkDoubleDecimal.includes(true)
  };
};

function handleMarkerClick(id:any, type:string, currentMarker:any, markers:any, google:any, actions:any) {
  const newMarker = MarkerIconGmap(google, '#f20072', currentMarker.type);
  // Set all markers back to normal.
  markers.map((marker:any) => {
    const inactiveMarker = MarkerIconGmap(google, '#1d9ad4', marker.type);
    if (marker.labelClass === 'marker--distance marker--distance__active') {
      marker.labelClass = 'marker--distance';
      marker.label.draw();
    }
    marker.setIcon(inactiveMarker);
    return true;
  });
  // Set the current marker as active.
  currentMarker.setIcon(newMarker);
  if (currentMarker.labelClass === 'marker--distance') {
    setTimeout(() => {
      currentMarker.labelClass = 'marker--distance marker--distance__active';
      currentMarker.label.draw();
    }, 20);
  }
  // Set active marker type (route or poi)
  actions.setActiveMarker(id, type);
}

const setMarkers = (google:any, content:any, locale:string, actions:any) => {
  // Set the marker for the africa museum.
  const iconKmma = africaIcon(google);
  const africaMarker = new MarkerWithLabel({
    map: google.map,
    position: { lat: 50.830946, lng: 4.518377 },
    icon: iconKmma.icon,
    zIndex: 9999,
    labelContent: iconKmma.label,
    labelAnchor: BIG_SCREEN ? new google.maps.Point(9, 65) : new google.maps.Point(6, 52),
    labelClass: 'icon--africa',
  });
  // Set markers for each location.
  const markers = content && content.map((item:any) => {
    const type = item.field_type_route || '';
    const icon = MarkerIconGmap(google, '#1d9ad4', type);
    const marker = new MarkerWithLabel({
      map: google.map,
      position: { lat: parseFloat(item.field_coord_latitude), lng: parseFloat(item.field_coord_longitude) },
      icon,
      type
    });
    // Add distance to marker if it's set.
    if (item.field_afstand && item.field_afstand.length > 0) {
      // Determine the horizontal position of the text block under the marker based on string length of distance.
      const { decimal, double, doubleDecimal } = getNumberLengthOfDistance(item.field_afstand);

      let posX;

      if (BIG_SCREEN) {
        posX = (doubleDecimal) ? 30 : (double || decimal) ? 26 : 20;
      } else {
        posX = (doubleDecimal) ? 25 : (double || decimal) ? 22 : 18;
      }

      // Combine distances to one string.
      const combinedDistances = item.field_afstand.reduce((acc:string, current:string) => {
        const formatted = `${parseFloat(current).toLocaleString(locale)} km`;
        return acc + formatted + '<br />';
      }, '');

      marker.labelContent = combinedDistances;
      marker.labelAnchor = new google.maps.Point(posX, -5);
      marker.labelClass = "marker--distance";
    }

    // Add on click event handler to make the marker active.
    google.maps.event.addListener(marker, 'click', () => handleMarkerClick(item.nid, item.type, marker, markers, google, actions));

    return marker;
  });
  // Group markers that are too close to eachother.
  const markerCluster = setMarkerCluster(google, markers);
};

// Cluster markers that are close together.
const setMarkerCluster = (google:any, markers:any) => {
  return new MarkerClusterer(google.map, markers, {
    styles: [{
      width: BIG_SCREEN ? 70 : 50,
      height: BIG_SCREEN ? 70 : 50,
      url: 'data:image/svg+xml;base64,' + btoa(ClusterMarker),
      textColor: 'white',
      textSize: BIG_SCREEN ? 24 : 16,
    }],
  });
};

// Get the center of 2 points.
const getCenter = (lowest:number, highest:number) => {
  return lowest + ((highest - lowest) / 2);
};

const Map = ({ history, ...props }:any) => {
  const { state, actions } = useContext(StoreContext);
  const [zoom, setZoom] = useState(props.zoom);
  const [googleState, setGoogleState] = useState<any>({});
  const brusselsCoords:any = useFetch(`regio/coords/2`, false);
  const { content, center } = props;
  const { polygons } = props;

  // Set the zoom level on mount.
  useEffect(() => {
    setZoom(getZoomLevelByRegion(props.match.params.region.toString()));
  }, []);

  // If the region we're on is Groene Gordel & Leuven, we get the polygon of Brussels to create the cutout in the main polygon.
  useEffect(() => {
    // When google api is loaded, we draw the regions.
    if (googleState.map && brusselsCoords.length > 0) {
      const negativePolygon = props.region === "3" && brusselsCoords.length > 0 ? JSON.parse(brusselsCoords[0].field_coordinates).reverse() : [];
      setRegionsOnMap(googleState, polygons, negativePolygon);
    }

  }, [googleState, brusselsCoords]);

  // When the map is loaded, we draw the region(s) and markers.
  const handleLoad = (google:any) => {
    setGoogleState(google);
    setMarkers(google, content, state.localeAbbr, actions);
  };

  // Center map on the center of the polygon (region).
  const handleChange = () => {
    let polygon;
    let lowestX = 100;
    let lowestY = 100;
    let highestX = 0;
    let highestY = 0;

    if (typeof polygons !== 'object') {
      polygon = JSON.parse(polygons);
    } else {
      const jsonPolygons = polygons.map((coords:any) => JSON.parse(coords));
      polygon = jsonPolygons.flat();
    }

    // Get the bounds of the bouding rectangle.
    polygon.map((coords:any) => {
      lowestX = (lowestX < coords.lat) ? lowestX : coords.lat;
      lowestY = (lowestY < coords.lng) ? lowestY : coords.lng;
      highestX = (highestX > coords.lat) ? highestX : coords.lat;
      highestY = (highestY > coords.lng) ? highestY : coords.lng;
      return true;
    });

    // Get the center of the bounds.
    const centerLat = getCenter(lowestX, highestX);
    const centerLng = getCenter(lowestY, highestY);

    center.lat = centerLat;
    center.lng = centerLng;
    // props.zoom = getZoomLevelByRegion(props.match.params.region.toString());
  };

  return (
    <div className="map-wrapper">
      <GoogleMapReact
        bootstrapURLKeys={{ key: 'AIzaSyCvCSKglBIkW0ZdASK9SwgWMjtioGvUV78' }}
        defaultCenter={center}
        defaultZoom={props.zoom}
        zoom={zoom}
        yesIWantToUseGoogleMapApiInternals={true}
        onGoogleApiLoaded={handleLoad}
        onChange={handleChange}
        options={{
          styles: [{ "featureType":"all","elementType":"labels.text.fill","stylers":[{ "saturation":36 },{ "color":"#333333" },{ "lightness":40 }] },{ "featureType":"all","elementType":"labels.text.stroke","stylers":[{ "visibility":"on" },{ "color":"#ffffff" },{ "lightness":16 }] },{ "featureType":"all","elementType":"labels.icon","stylers":[{ "visibility":"off" }] },{ "featureType":"administrative","elementType":"geometry.fill","stylers":[{ "color":"#fefefe" },{ "lightness":20 }] },{ "featureType":"administrative","elementType":"geometry.stroke","stylers":[{ "color":"#fefefe" },{ "lightness":17 },{ "weight":1.2 }] },{ "featureType":"administrative.province","elementType":"all","stylers":[{ "visibility":"off" }] },{ "featureType":"administrative.locality","elementType":"all","stylers":[{ "visibility":"on" }] },{ "featureType":"landscape","elementType":"geometry","stylers":[{ "color":"#f5f5f5" },{ "lightness":20 }] },{ "featureType":"poi","elementType":"geometry","stylers":[{ "color":"#f5f5f5" },{ "lightness":21 }] },{ "featureType":"poi.park","elementType":"geometry","stylers":[{ "color":"#dedede" },{ "lightness":21 }] },{ "featureType":"road.highway","elementType":"geometry.fill","stylers":[{ "lightness":17 },{ "color":"#eeeeee" }] },{ "featureType":"road.highway","elementType":"geometry.stroke","stylers":[{ "lightness":29 },{ "weight":0.2 },{ "color":"#eeeeee" }] },{ "featureType":"road.highway","elementType":"labels.icon","stylers":[{ "visibility":"on" },{ "saturation":"-100" },{ "lightness":"40" }] },{ "featureType":"road.arterial","elementType":"geometry","stylers":[{ "color":"#ffffff" },{ "lightness":18 }] },{ "featureType":"road.arterial","elementType":"geometry.fill","stylers":[{ "color":"#eeeeee" }] },{ "featureType":"road.arterial","elementType":"geometry.stroke","stylers":[{ "color":"#eeeeee" }] },{ "featureType":"road.local","elementType":"geometry","stylers":[{ "color":"#ffffff" },{ "lightness":16 }] },{ "featureType":"transit","elementType":"geometry","stylers":[{ "color":"#f2f2f2" },{ "lightness":19 }] },{ "featureType":"water","elementType":"geometry","stylers":[{ "color":"#e9e9e9" },{ "lightness":17 }] }],
          zoomControlOptions: {
            position: 4
          },
          minZoom: 10,
          clickableIcons: false,
          fullscreenControl: false
        }}
      />
    </div>
  );
};

Map.defaultProps = {
  center: {
    lat: 51.05361111,
    lng: 4.54694444
  },
  zoom: 12,
};

export default withRouter(Map);